import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Briefcase, UserRound, ArrowRight, ArrowLeft, Shield, Zap, Sparkles } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

const AccediScelta = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-[100dvh] bg-[#0a1628] text-slate-100 antialiased relative overflow-hidden safe-top safe-bottom">
      <div className="fixed inset-0 bg-gradient-to-br from-[#0a1628] via-[#0d1f3c] to-[#0a0f1c] pointer-events-none" />
      <div className="fixed top-1/4 -left-20 w-96 h-96 bg-sky-500/10 rounded-full blur-[100px] pointer-events-none" />
      <div className="fixed bottom-1/4 -right-20 w-96 h-96 bg-cyan-500/10 rounded-full blur-[100px] pointer-events-none" />
      <div
        className="fixed inset-0 opacity-30 pointer-events-none"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.06) 1px, transparent 1px)`,
          backgroundSize: '40px 40px',
          maskImage: 'radial-gradient(ellipse at center, black 20%, transparent 70%)',
          WebkitMaskImage: 'radial-gradient(ellipse at center, black 20%, transparent 70%)',
        }}
      />

      <header className="relative max-w-6xl mx-auto px-4 sm:px-6 h-16 mt-2 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="brand-logo">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-sky-500/20 border border-sky-500/30">
            <Zap className="w-5 h-5 text-sky-400" />
          </div>
          <div className="leading-tight">
            <div className="font-bold text-sm sm:text-base">F.lli Schilirò Servizi</div>
            <div className="text-[10px] sm:text-xs text-sky-300/70 -mt-0.5">Area Riservata</div>
          </div>
        </Link>
        <Link to="/" className="text-xs sm:text-sm px-3 py-1.5 rounded-full border border-white/10 hover:border-sky-400/30 hover:bg-sky-500/5 transition-colors inline-flex items-center gap-1.5" data-testid="back-home">
          <ArrowLeft className="w-3.5 h-3.5" />
          Torna alla home
        </Link>
      </header>

      <main className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-10 sm:pt-16 pb-20">
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-full bg-sky-500/10 border border-sky-500/20 text-sky-300 mb-5">
            <Shield className="w-3.5 h-3.5 text-emerald-400" />
            Accesso riservato
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
            Benvenuto. <br />
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-sky-400 to-cyan-300">
              Scegli come accedere.
            </span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-400">
            Due aree, due esperienze: una per i nostri consulenti, una per te che vuoi gestire casa e consumi.
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-2 gap-5 max-w-4xl mx-auto">
          {/* Agente */}
          <Card
            data-testid="card-agente"
            className="group relative overflow-hidden bg-slate-900/70 border-white/10 hover:border-sky-400/40 transition-all cursor-pointer"
            onClick={() => navigate('/login')}
          >
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-sky-500/15 blur-3xl group-hover:bg-sky-500/25 transition" />
            <CardContent className="relative p-7 sm:p-8">
              <div className="w-14 h-14 rounded-2xl bg-sky-500/15 border border-sky-500/30 grid place-items-center">
                <Briefcase className="w-7 h-7 text-sky-300" />
              </div>
              <div className="mt-5 flex items-center gap-2">
                <h2 className="text-xl sm:text-2xl font-bold text-white">Sono un Agente</h2>
              </div>
              <p className="mt-2 text-sm text-slate-400">
                Entra nel CRM per gestire clienti, pratiche, provvigioni e tutta la tua rete di vendita.
              </p>
              <ul className="mt-5 space-y-2 text-sm text-slate-300">
                <li className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-sky-400" /> Dashboard commissioni</li>
                <li className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-sky-400" /> Gestione lead e contratti</li>
                <li className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-sky-400" /> Comparatore AI integrato</li>
              </ul>
              <Button
                data-testid="btn-login-agente"
                className="w-full mt-6 h-11 bg-sky-500 hover:bg-sky-400 text-white font-semibold shadow-lg shadow-sky-500/20"
                onClick={(e) => { e.stopPropagation(); navigate('/login'); }}
              >
                Accedi al CRM
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <p className="mt-3 text-[11px] text-slate-500 text-center">
                Registrazione non consentita — gli account sono creati dall'amministratore.
              </p>
            </CardContent>
          </Card>

          {/* Cliente */}
          <Card
            data-testid="card-cliente"
            className="group relative overflow-hidden bg-slate-900/70 border-white/10 hover:border-cyan-400/40 transition-all cursor-pointer"
            onClick={() => navigate('/clienti/login')}
          >
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-cyan-500/10 blur-3xl group-hover:bg-cyan-500/20 transition" />
            <CardContent className="relative p-7 sm:p-8">
              <div className="w-14 h-14 rounded-2xl bg-cyan-500/15 border border-cyan-500/30 grid place-items-center">
                <UserRound className="w-7 h-7 text-cyan-300" />
              </div>
              <div className="mt-5 flex items-center gap-2">
                <h2 className="text-xl sm:text-2xl font-bold text-white">Sono un Cliente</h2>
              </div>
              <p className="mt-2 text-sm text-slate-400">
                Carica le tue bollette, controlla le scadenze delle offerte e richiedi un nuovo preventivo
                in un clic.
              </p>
              <ul className="mt-5 space-y-2 text-sm text-slate-300">
                <li className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-cyan-400" /> Le tue utenze luce / gas / fibra</li>
                <li className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-cyan-400" /> OCR automatico delle bollette</li>
                <li className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-cyan-400" /> Alert scadenza offerta</li>
              </ul>
              <Button
                data-testid="btn-login-cliente"
                className="w-full mt-6 h-11 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold shadow-lg shadow-cyan-500/20"
                onClick={(e) => { e.stopPropagation(); navigate('/clienti/login'); }}
              >
                Accedi all'Area Clienti
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <p className="mt-3 text-[11px] text-slate-500 text-center">
                Non hai un account?{' '}
                <Link to="/clienti/registrati" className="text-cyan-300 underline" onClick={(e) => e.stopPropagation()}>
                  Registrati gratis
                </Link>
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="mt-10 text-center text-xs text-slate-500">
          Problemi con l'accesso? Contatta il tuo responsabile di rete.
        </div>
      </main>
    </div>
  );
};

export default AccediScelta;
